/**
 * Serviço de IA - Chamadas seguras via Cloud Functions
 * A API Key do Gemini agora está protegida no backend
 */

import { auth } from '../firebase/config';
import { AtsRobotResult, JobMatchResult, OptimizedResume } from "../types";

// URL base das Cloud Functions
const getBaseUrl = (): string => {
  try {
    // @ts-ignore
    if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_URL) {
      // @ts-ignore
      return import.meta.env.VITE_API_URL;
    }
  } catch (e) { /* ignore */ }

  return "https://us-central1-meu_projeto.cloudfunctions.net";
};

const BASE_URL = getBaseUrl();

/**
 * Helper para fazer chamadas autenticadas às Cloud Functions
 */
const callGeminiFunction = async <T>(functionName: string, body: Record<string, any>): Promise<T> => {
  const token = await auth.currentUser?.getIdToken();

  if (!token) {
    throw new Error("Usuário não autenticado. Faça login para continuar.");
  }

  const response = await fetch(`${BASE_URL}/${functionName}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(body)
  });

  if (!response.ok) {
    let errorMessage = 'Erro ao processar requisição';
    try {
      const errorData = await response.json();
      errorMessage = errorData.error || errorMessage;
    } catch (e) {
      errorMessage = await response.text() || errorMessage;
    }
    throw new Error(errorMessage);
  }

  return response.json();
};

// A5: analyzeResume removido — endpoint morto (nenhum componente chamava) e sem cota no backend.

/**
 * Match detalhado entre currículo e vaga
 */
export const analyzeJobMatch = async (resumeText: string, jobDescription: string): Promise<JobMatchResult> => {
  try {
    return await callGeminiFunction<JobMatchResult>('analyzeJobMatch', {
      resumeText,
      jobDescription
    });
  } catch (error) {
    console.error("Error matching job:", error);
    throw error;
  }
};

/**
 * Match entre currículo e vaga anônimo (sem login)
 */
export const analyzeJobMatchAnonymous = async (resumeText: string, jobDescription: string): Promise<JobMatchResult> => {
  const response = await fetch(`${BASE_URL}/analyzeJobMatchAnonymous`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ resumeText, jobDescription })
  });

  if (!response.ok) {
    let errorMessage = 'Erro ao processar requisição';
    try {
      const errorData = await response.json();
      errorMessage = errorData.error || errorMessage;
    } catch (e) {
      errorMessage = await response.text() || errorMessage;
    }
    throw new Error(errorMessage);
  }

  return response.json();
};

/**
 * Análise de PDF (parsability)
 */
export const analyzePdfResume = async (base64Pdf: string): Promise<AtsRobotResult> => {
  try {
    return await callGeminiFunction<AtsRobotResult>('analyzePdfResume', {
      base64Pdf
    });
  } catch (error) {
    console.error("Error parsing PDF:", error);
    throw error;
  }
};

/** Campos extras retornados apenas para scans anônimos (gate + soft limit). */
export interface AnonScanMeta {
  softLimitWarning: boolean;  // true = último scan gratuito disponível
  remainingScans: number;     // quantos scans ainda restam na janela de 1h
}

/**
 * Análise de PDF anônima (sem login) — Rate limited por IP + cache SHA-256.
 * Retorna AtsRobotResult com campos extras de controle de limite.
 * Lança erro com message="ANON_LIMIT_REACHED" quando o limite é atingido.
 */
export const analyzePdfResumeAnonymous = async (base64Pdf: string): Promise<AtsRobotResult & AnonScanMeta> => {
  const response = await fetch(`${BASE_URL}/analyzePdfResumeAnonymous`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ base64Pdf })
  });

  if (!response.ok) {
    let errorMessage = 'Erro ao processar requisição';
    try {
      const errorData = await response.json();
      errorMessage = errorData.error || errorMessage;
    } catch (e) {
      errorMessage = await response.text() || errorMessage;
    }
    throw new Error(errorMessage);
  }

  return response.json();
};

/**
 * Geração de currículo otimizado
 */
export const generateOptimizedResume = async (
  resumeText: string,
  jobDescription: string,
  refinementInstructions?: string
): Promise<OptimizedResume> => {
  try {
    return await callGeminiFunction<OptimizedResume>('generateOptimizedResume', {
      resumeText,
      jobDescription,
      refinementInstructions
    });
  } catch (error) {
    console.error("Error generating optimized resume:", error);
    throw error;
  }
};

/**
 * Geração anônima de currículo (sem login — 1 geração gratuita)
 */
export const generateOptimizedResumeAnonymous = async (
  resumeText: string,
  jobDescription: string,
  fingerprint: string
): Promise<OptimizedResume> => {
  const response = await fetch(`${BASE_URL}/generateOptimizedResumeAnonymous`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ resumeText, jobDescription, fingerprint })
  });

  if (!response.ok) {
    let errorMessage = 'Erro ao processar requisição';
    try {
      const errorData = await response.json();
      errorMessage = errorData.error || errorMessage;
    } catch (e) {
      errorMessage = await response.text() || errorMessage;
    }
    throw new Error(errorMessage);
  }

  return response.json();
};
